import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../OBWWizard';
import { portalApi } from '../../lib/portal-api';
import { DataStoredIn } from '../../components/DataResidency';

// Phase 5c.5 (ADR-030 D2 Step 11) — polling indicator (animated dot/spinner)
// + "Detected!" success state with tick icon + activation timeline showing
// progress (snippet installed → first beacon → dashboard live).

const POLL_INTERVAL_MS = 5000;

export function StepActivation({ accountId, domainToken, data, onNext }: StepProps) {
  const [status, setStatus] = useState<'waiting' | 'detected' | 'error'>('waiting');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // PL16 — in-portal add-plan / add-domain journeys END on a snippet modal
  // showing the embed code to copy (not the blocking "waiting for beacon"
  // dead-end). The mount opts in via initialStepData.activation.revealSnippet —
  // data-driven, no journeyId sniffing in the engine (ADR-077 L5). Sign-up keeps
  // the email-delivered snippet (ADR-076 §5), so it never sets this flag.
  const revealSnippet = data?.revealSnippet === true;
  // Format matches the DEPLOYED track.js Worker (workers/track-js/src/index.ts):
  // the token is read from the `?id=` query param via document.currentScript.src,
  // NOT a `data-token` attribute (that form is silently ignored — no tracking).
  // Identical to admin-api's snippet and the signed-off prototype.
  const snippetTag = domainToken
    ? `<script async src="https://cdn.nexanomics.com/track.js?id=${domainToken}"></script>`
    : '';
  const [copied, setCopied] = useState(false);
  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippetTag);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard denied — the code is visible to select and copy manually.
    }
  }

  useEffect(() => {
    // D4 — only the in-portal snippet ending listens for a beacon. The sign-up's completion
    // screen promises nothing it would have to watch for (the onboarding hub does that).
    if (!revealSnippet || !accountId || !domainToken) return;
    async function checkBeacon() {
      try {
        // ⛔ 401'd FOR ITS ENTIRE LIFE (ADR-107 §1.2). `credentials: 'include'`
        // with no Bearer against an authorized route — so this poll could never
        // detect the first beacon, and the activation step never flipped to
        // "detected" for anybody. Silent, because a poll that always fails looks
        // exactly like a snippet that has not been installed yet.
        const { status, data } = await portalApi<{ domainSetupState?: string }>(
          `/portal/obw/${accountId}/domains/${domainToken}/activation-status`,
          { allowedStatuses: [400, 404] },
        );
        if (status === 200 && data) {
          const d = data;
          if (d.domainSetupState === 'complete') {
            setStatus('detected');
            if (pollingRef.current) clearInterval(pollingRef.current);
          }
        }
      } catch {
        // Non-fatal — keep polling
      }
    }
    pollingRef.current = setInterval(() => void checkBeacon(), POLL_INTERVAL_MS);
    void checkBeacon();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [revealSnippet, accountId, domainToken]);

  function handleContinue() {
    onNext({ beaconDetected: status === 'detected' });
  }

  // PL16 — snippet-modal ending for the in-portal journeys. Shows the copyable
  // embed code so the subscriber can install + activate immediately; the "Done"
  // action is always available (no beacon gate). Email backup still fires
  // post-activation (ADR-076 §5), so the code arrives by email too.
  if (revealSnippet) {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Install your tracking snippet</h2>
        <p className="obw-subtitle">
          Your plan is set. Add this snippet to your site to switch on Customer Journey
          Intelligence — paste it just before the closing <code>&lt;/head&gt;</code> tag on every
          page. {status === 'detected'
            ? 'We’ve already detected your first beacon — you’re live!'
            : 'We’ll start listening for the first beacon as soon as it’s installed.'}
        </p>

        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-snippet-code">Embed code</label>
          {/* ⭐ THE "BLACK-ON-BLACK" FIELD WAS NEVER A CONTRAST BUG. The <pre> below
              paints #e2e8f0 on #0f172a (~14:1 — comfortably above the AA minimums in
              CLAUDE.md). It looked black-on-black because `snippetTag` is '' whenever
              `domainToken` is absent, so the user was shown a LABELLED, EMPTY, near-black
              box and reasonably read it as invisible text.
              An empty box that looks like a broken field is a lie about state — so the
              no-token case now says so plainly instead of rendering a void. The token
              itself is restored by the domain-capture fix (the `sign-up` journey now mints
              a Domain at domain-verify); this is the honest fallback for every other way
              a token can be missing. */}
          {snippetTag ? (
            <pre
              id="obw-snippet-code"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#e2e8f0',
                background: '#0f172a',
                borderRadius: '10px',
                padding: '14px 16px',
                margin: '0 0 8px',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {snippetTag}
            </pre>
          ) : (
            <p
              id="obw-snippet-code"
              className="obw-form-hint"
              style={{ margin: '0 0 8px', fontSize: '14px' }}
            >
              We couldn’t load your tracking snippet yet — your website hasn’t been linked to
              this account. Add it from Manage Account, and we’ll email the snippet to you.
            </p>
          )}
          {/* ADR-124 I6 — where the data this snippet sends is stored, beside the snippet. */}
          {snippetTag && <DataStoredIn className="obw-residency-consequence" />}
          <p className="obw-form-hint">
            We’ve also emailed this snippet to you, so you can forward it to whoever manages your
            site.
          </p>
        </div>

        <div className="portal-wizard-nav" style={{ display: 'flex', gap: '10px' }}>
          <button type="button" className="obw-btn-secondary" onClick={() => void copySnippet()}>
            {copied ? '✓ Copied' : 'Copy snippet'}
          </button>
          <button type="button" className="obw-btn-primary" onClick={handleContinue}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // ⭐⭐ D4 (Jeff, 5 October 2026) — THE SIGN-UP'S LAST SCREEN TELLS THE TRUTH ABOUT WHAT HAPPENS
  // NEXT. It used to be "Activation": a timeline whose first node read "Snippet copied ✓" and a
  // spinner listening for a first beacon. But the snippet step was removed from this journey
  // (ADR-073 A2.3) — the snippet is EMAILED (ADR-076 §5) — so the customer had copied nothing and
  // there was nothing yet to listen for. This is now a completion screen: the next step is
  // installing the snippet, which has been emailed to the address shown; the install guide is
  // on the onboarding page; Done. No fake tick, no listening spinner.
  //
  // The address and the guide link are DATA from the mount (`snippetEmailTo`,
  // `installGuideUrl` on `initialStepData.activation`) — the step owns no route and no fetch.
  const emailTo = typeof data?.snippetEmailTo === 'string' ? data.snippetEmailTo : '';
  const guideUrl = typeof data?.installGuideUrl === 'string' ? data.installGuideUrl : '';
  return (
    <div className="obw-step-content" data-testid="obw-signup-complete">
      <h2 className="obw-title">You’re all set — one step left</h2>
      <p className="obw-subtitle">
        Your plan and details are saved. The last step is adding your tracking snippet to your
        website — one line of code that your web designer (or you) adds once.
      </p>

      <div className="obw-ai-status" role="status">
        <span className="obw-ai-done" aria-hidden="true">✉</span>
        <span>
          {emailTo ? (
            <>We’ve emailed your snippet and install steps to <strong style={{ color: 'var(--text)' }}>{emailTo}</strong>.</>
          ) : (
            <>We’ve emailed your snippet and install steps to your account email address.</>
          )}
        </span>
      </div>

      <p className="obw-subtitle" style={{ fontSize: '16px' }}>
        Forward that email to whoever manages your website, or open your onboarding page to copy
        the snippet and send it to your web designer from there.
        {' '}It shows when your first data arrives.
      </p>
      {guideUrl && (
        <p className="obw-subtitle" style={{ fontSize: '16px' }}>
          <a href={guideUrl} style={{ color: 'var(--accent)' }} data-testid="obw-install-guide-link">
            Read the install guide
          </a>
        </p>
      )}

      <div className="portal-wizard-nav">
        <button type="button" className="obw-btn-primary" onClick={handleContinue}>
          Done
        </button>
      </div>
    </div>
  );
}
